import { useState, useEffect } from "react";

const ViewTask = ({ isOpen, task, onClose, onSave }) => {
  const [editableTask, setEditableTask] = useState("");
  const [status, setStatus] = useState("Pending");
  const [resources, setResources] = useState([]);
  const [newResource, setNewResource] = useState({ name: "", link: "" });
  const [isEditingResources, setIsEditingResources] = useState(false);

  useEffect(() => {
    if (task) {
      setEditableTask(task.task || "");
      setStatus(task.status || "Pending");
      setResources(task.resources || []);
      setIsEditingResources(false);
    }
  }, [task]);

  if (!isOpen || !task) return null;

  const start = new Date(task.startTime);
  const end = new Date(task.endTime);

  const formatTime = (date) =>
    date
      .toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      .toLowerCase();

  const displayDate = `${start.toLocaleDateString("en-US", {
  weekday: "short",
  month: "short",     // Add this line to include month
  day: "numeric",
  year: "numeric",
})} — ${formatTime(start)} to ${formatTime(end)}`;

  const handleResourceChange = (index, field, value) => {
    const updated = [...resources];
    updated[index][field] = value;
    setResources(updated);
  };

  const handleAddResource = () => {
    const trimmedName = newResource.name.trim();
    const trimmedLink = newResource.link.trim();
    if (trimmedName && trimmedLink) {
      setResources([...resources, { name: trimmedName, link: trimmedLink }]);
      setNewResource({ name: "", link: "" });
    }
  };

  const handleDeleteResource = (index) => {
    const updated = [...resources];
    updated.splice(index, 1);
    setResources(updated);
  };

  const handleSave = async () => {
    const updatedTask = {
      ...task,
      task: editableTask.trim(),
      status,
      resources,
    };
    let res = await fetch(`${process.env.REACT_APP_NODEURL}/updateTask`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ task: updatedTask }),
    });

    let ans = await res.json();
    if (ans.msg == "success") {
      onSave(ans.res,false);
      onClose();
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this task?")) {
      let res = await fetch(`${process.env.REACT_APP_NODEURL}/deleteTask`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ taskId: task.id }),
      });

      let ans = await res.json();
      if (ans.msg === "success") {
        onClose();
        onSave(task.id,true)
      }
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/80 z-50 backdrop-blur-sm">
      <div className="bg-[#0f0f1a] rounded-xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-hidden flex flex-col border border-purple-900/50">
        {/* Header */}
        <div className="bg-[#161622] px-6 py-4 border-b border-purple-900/30">
          <h2 className="text-left text-lg font-medium text-purple-300/90 tracking-wide">
            {displayDate}
          </h2>
        </div>

        {/* Body */}
        <div className="overflow-y-auto px-6 py-5 flex-1 space-y-5">
          {/* Task */}
          <div>
            <label className="block text-xs font-medium text-left text-purple-300/80 mb-2 tracking-wider uppercase">
              Task
            </label>
            <textarea
              className="w-full p-3 text-sm rounded-lg bg-[#161622] text-purple-100 border border-purple-900/50 focus:outline-none focus:ring-1 focus:ring-purple-500/50 focus:border-purple-500/50 resize-none placeholder-purple-900/70"
              rows={3}
              value={editableTask}
              onChange={(e) => setEditableTask(e.target.value)}
              placeholder="Describe your task..."
            />
          </div>

          {/* Status */}
          <div>
            <label className="block text-xs font-medium text-left text-purple-300/80 mb-2 tracking-wider uppercase">
              Status
            </label>
            <div className="flex gap-4">
              {["Pending", "Progress", "Completed"].map((opt) => (
                <label
                  key={opt}
                  className="flex items-center gap-2 text-sm text-purple-100/90"
                >
                  <div className="flex items-center">
                    <input
                      type="radio"
                      name="status"
                      value={opt}
                      checked={status === opt}
                      onChange={(e) => setStatus(e.target.value)}
                      className="opacity-0 absolute h-4 w-4"
                    />
                    <div
                      className={`w-4 h-4 rounded-full border ${
                        status === opt
                          ? "border-purple-500 bg-purple-500/20"
                          : "border-purple-700"
                      } flex items-center justify-center`}
                    >
                      {status === opt && (
                        <div className="w-2 h-2 rounded-full bg-purple-400"></div>
                      )}
                    </div>
                  </div>
                  {opt}
                </label>
              ))}
            </div>
          </div>

          {/* Resources */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-purple-300/80 tracking-wider uppercase">
                Resources
              </span>
              {resources.length > 0 && (
                <button
                  className="text-xs text-purple-400/80 hover:text-purple-300 transition-colors"
                  onClick={() => setIsEditingResources((prev) => !prev)}
                >
                  {isEditingResources ? "Done" : "Edit"}
                </button>
              )}
            </div>

            <ul className="space-y-2.5">
              {resources.map((item, index) => (
                <li key={index} className="flex items-center gap-2">
                  {isEditingResources ? (
                    <div className="flex items-center gap-2 w-full">
                      <input
                        type="text"
                        value={item.name}
                        onChange={(e) =>
                          handleResourceChange(index, "name", e.target.value)
                        }
                        className="flex-1 p-2 text-sm rounded bg-[#161622] text-purple-100 border border-purple-900/50 focus:outline-none focus:ring-1 focus:ring-purple-500/50 focus:border-purple-500/50"
                        placeholder="Resource name"
                      />
                      <input
                        type="text"
                        value={item.link}
                        onChange={(e) =>
                          handleResourceChange(index, "link", e.target.value)
                        }
                        className="flex-1 p-2 text-sm rounded bg-[#161622] text-purple-100 border border-purple-900/50 focus:outline-none focus:ring-1 focus:ring-purple-500/50 focus:border-purple-500/50"
                        placeholder="https://"
                      />
                      <button
                        className="p-2 text-purple-500/70 hover:text-red-400 transition-colors"
                        onClick={() => handleDeleteResource(index)}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <a
                      href={item.link}
                      className="text-sm text-purple-400/90 hover:text-purple-300 transition-colors flex items-center gap-1.5"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                        />
                      </svg>
                      <span className="truncate max-w-xs">{item.name}</span>
                    </a>
                  )}
                </li>
              ))}
            </ul>

            {/* Add new resource */}
            <div className="flex gap-2 mt-3">
              <input
                type="text"
                value={newResource.name}
                onChange={(e) =>
                  setNewResource({ ...newResource, name: e.target.value })
                }
                className="flex-1 p-2 text-sm rounded bg-[#161622] text-purple-100 border border-purple-900/50 focus:outline-none focus:ring-1 focus:ring-purple-500/50 focus:border-purple-500/50"
                placeholder="Resource name"
              />
              <input
                type="text"
                value={newResource.link}
                onChange={(e) =>
                  setNewResource({ ...newResource, link: e.target.value })
                }
                className="flex-1 p-2 text-sm rounded bg-[#161622] text-purple-100 border border-purple-900/50 focus:outline-none focus:ring-1 focus:ring-purple-500/50 focus:border-purple-500/50"
                placeholder="https://"
              />
              <button
                className={`p-2 rounded transition-colors ${
                  newResource.name.trim() && newResource.link.trim()
                    ? "text-purple-400/90 hover:text-purple-300 hover:bg-purple-900/20"
                    : "text-purple-900/50 cursor-not-allowed"
                }`}
                onClick={handleAddResource}
                disabled={!newResource.name.trim() || !newResource.link.trim()}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center px-6 py-4 border-t border-purple-900/30 bg-[#161622]">
          <button
            onClick={handleDelete}
            className="px-4 py-2 text-sm rounded-md text-red-400 hover:text-red-300 hover:bg-red-900/30 transition-colors border border-red-900/50"
          >
            Delete Task
          </button>

          <div className="flex gap-3">
            <button
              className="px-4 py-2 text-sm rounded-md text-purple-300/90 hover:text-purple-200 hover:bg-gray-900/30 transition-colors border border-gray-700"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 text-sm rounded-md bg-purple-900/60 text-purple-100 hover:bg-purple-800/60 transition-colors border border-purple-800/50 shadow-[0_1px_1px_0px_rgba(139,92,246,0.2)]"
              onClick={handleSave}
            >
              Save Task
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewTask;
